/**
 * THE PUBLIC TERMINAL LEDGER DTOs — wire-safe terminal facts.
 *
 * Split out of `ask-helix.ts` (PR #8527 cure round 2): that file is pinned
 * debt under the repo size law and may only shrink. Verbatim move; the names
 * remain re-exported from `ask-helix.ts`, so every existing import path
 * resolves unchanged.
 */
import type { AskHelixFigureProvenanceDto } from '../../ask-helix';

/** A server-issued public alias; never an internal obligation or intent id. */
export type AskHelixPublicTerminalLedgerIdDto = string;

export const ASK_HELIX_PUBLIC_TERMINAL_LEDGER_REASON_CODES = [
  'ambiguous_request',
  'discovery_insufficient',
  'missing_capability',
  'missing_data',
  'permission_denied',
  'invalid_plan',
  'execution_failed',
  'no_progress',
  'runtime_budget_exceeded',
  'infeasible_under_constraints',
  'approval_required',
  'unbound_numeric_binding',
] as const;

export type AskHelixPublicTerminalLedgerReasonCodeDto =
  (typeof ASK_HELIX_PUBLIC_TERMINAL_LEDGER_REASON_CODES)[number];

export type AskHelixPublicTerminalCoverageWitnessDto = Readonly<{
  publicId: AskHelixPublicTerminalLedgerIdDto;
}>;

export type AskHelixPublicTerminalUnresolvedDto = Readonly<{
  publicId: AskHelixPublicTerminalLedgerIdDto;
  reasonCode: AskHelixPublicTerminalLedgerReasonCodeDto;
}>;

export type AskHelixPublicTerminalCertificateDto = Readonly<{
  outcome: 'answered' | AskHelixPublicTerminalLedgerReasonCodeDto;
}>;

export type AskHelixPublicTerminalNumericBindingDto = Readonly<{
  publicId: AskHelixPublicTerminalLedgerIdDto;
  displayValue: string | null;
  displayValueCompact: string | null;
  unit: string;
  provenance: AskHelixFigureProvenanceDto | null;
}>;

/**
 * Reducer-sealed terminal facts for every publication branch. The server maps
 * internal identities into these public aliases before this DTO is constructed.
 */
export type AskHelixPublicTerminalLedgerDto = Readonly<{
  integrity: 'sealed';
  certificate: AskHelixPublicTerminalCertificateDto;
  covered: readonly AskHelixPublicTerminalCoverageWitnessDto[];
  unresolved: readonly AskHelixPublicTerminalUnresolvedDto[];
  numericBindings: readonly AskHelixPublicTerminalNumericBindingDto[];
}>;
